import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import Alert from '../Alert/Alert';
import AddressForm from './AddressForm';

const MAX_ADDRESSES = 10;

const AddressBook = ({ data, isCheckout = false, selectedAddress, setSelectedAddress }) => {
    const [showAddressSelect, setShowAddressSelect] = useState(false);
    const [alertVisible, setAlertVisible] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertType, setAlertType] = useState('error');
    const [addresses, setAddresses] = useState(data || []);
    const [editingAddress, setEditingAddress] = useState(null);
    const [originalAddress, setOriginalAddress] = useState(null);

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm();

    useEffect(() => {
        if (editingAddress) {
            reset(editingAddress);
        } else {
            reset({
                AddressString: '',
                County: '',
                City: '',
                Index: '',
                Phone: '',
            });
        }
    }, [editingAddress, reset]);

    const showAlert = (message, type = 'error') => {
        setAlertMessage(message);
        setAlertType(type);
        setAlertVisible(true);
        setTimeout(() => setAlertVisible(false), 3000);
    };

    const onSubmit = async (address) => {
        if (!editingAddress?.Id && addresses.length >= MAX_ADDRESSES) {
            showAlert(`Нельзя добавить больше ${MAX_ADDRESSES} адресов`);
            return;
        }

        Object.keys(address).forEach((key) => {
            if (typeof address[key] === 'string') {
                address[key] = address[key].trim();
            }
        });

        if (editingAddress?.Id) {
            const response = await fetch('/Account/EditAddress', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...address }),
            });

            if (response.ok) {
                setAddresses((prevAddresses) =>
                    prevAddresses.map((addr) => (addr.Id === editingAddress.Id ? { ...address, Id: addr.Id } : addr))
                );
            } else {
                const resp = await response.json();
                showAlert(resp.message || 'Ошибка изменения адреса');
                return;
            }
        } else {
            const response = await fetch('/Account/AddAddress', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...address }),
            });

            if (response.ok) {
                const resp = await response.json();
                setAddresses((prevAddresses) => [...prevAddresses, { ...resp.address }]);
            } else {
                const resp = await response.json();
                showAlert(resp.message || 'Ошибка добавления адреса');
                return;
            }
        }

        setEditingAddress(null);
    };

    const handleEdit = (address) => {
        setOriginalAddress(address);
        setEditingAddress({ ...address });
        reset(address);
    };

    const handleCancel = () => {
        if (originalAddress) {
            reset(originalAddress);
        }
        setEditingAddress(null);
    };

    const currentAddress = addresses.find((addr) => addr.Id === selectedAddress);

    return (
        <section className="account-section">
            {alertVisible && <Alert message={alertMessage} type={alertType} onClose={() => setAlertVisible(false)} />}

            <div className="account-section__header">
                <h2 className="account-section__title">{isCheckout ? 'Адрес доставки' : 'Сохранённые адреса'}</h2>
                <p className="account-section__description">
                    Добавляйте адреса заранее, чтобы оформление заказа занимало меньше времени.
                </p>
            </div>

            {editingAddress === null ? (
                <div className="account-stack">
                    {!isCheckout ? (
                        <>
                            {addresses.length > 0 ? (
                                <div className="account-address-list">
                                    {addresses.map((address) => (
                                        <div key={address.Id} className="account-address-card">
                                            <div className="account-meta-list">
                                                <div className="account-meta-row">
                                                    <span className="account-meta-label">Адрес</span>
                                                    <span className="account-meta-value">
                                                        {address.AddressString}, {address.County ? `${address.County}, ` : ''}
                                                        {address.City}
                                                    </span>
                                                </div>
                                                <div className="account-meta-row">
                                                    <span className="account-meta-label">Индекс</span>
                                                    <span className="account-meta-value">{address.Index}</span>
                                                </div>
                                                <div className="account-meta-row">
                                                    <span className="account-meta-label">Телефон</span>
                                                    <span className="account-meta-value">{address.Phone}</span>
                                                </div>
                                            </div>
                                            <div className="account-actions">
                                                <button onClick={() => handleEdit(address)} className="account-button--ghost" type="button">
                                                    Изменить
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="account-panel">
                                    <p className="account-empty">Нет сохранённых адресов.</p>
                                </div>
                            )}

                            <div className="account-actions">
                                <button
                                    onClick={() => handleEdit({})}
                                    disabled={addresses.length >= MAX_ADDRESSES}
                                    className="account-button"
                                    type="button"
                                >
                                    Добавить новый адрес
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="account-panel">
                                <div className="account-panel__header">
                                    <div>
                                        <h3 className="account-panel__title">Выбор адреса</h3>
                                        <p className="account-panel__subtitle">Укажите, куда нужно доставить ваш заказ.</p>
                                    </div>
                                    <button
                                        onClick={() => setShowAddressSelect(!showAddressSelect)}
                                        className="account-button--text"
                                        type="button"
                                    >
                                        {showAddressSelect ? 'Скрыть' : 'Изменить'}
                                    </button>
                                </div>

                                {showAddressSelect ? (
                                    <div className="account-address-list">
                                        {addresses.length > 0 ? (
                                            addresses.map((address) => (
                                                <div
                                                    key={address.Id}
                                                    className={`account-address-card ${selectedAddress === address.Id ? 'is-selected' : ''}`}
                                                >
                                                    <div className="account-radio-row">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedAddress === address.Id}
                                                            onChange={() => setSelectedAddress(address.Id)}
                                                        />
                                                        <div className="account-meta-list">
                                                            <div className="account-meta-row">
                                                                <span className="account-meta-label">Адрес</span>
                                                                <span className="account-meta-value">
                                                                    {address.AddressString}, {address.County ? `${address.County}, ` : ''}
                                                                    {address.City}
                                                                </span>
                                                            </div>
                                                            <div className="account-meta-row">
                                                                <span className="account-meta-label">Индекс</span>
                                                                <span className="account-meta-value">{address.Index}</span>
                                                            </div>
                                                            <div className="account-meta-row">
                                                                <span className="account-meta-label">Телефон</span>
                                                                <span className="account-meta-value">{address.Phone}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="account-actions">
                                                        <button onClick={() => handleEdit(address)} className="account-button--ghost" type="button">
                                                            Изменить
                                                        </button>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <p className="account-empty">Нет сохранённых адресов.</p>
                                        )}
                                    </div>
                                ) : currentAddress ? (
                                    <div className="account-meta-list">
                                        <div className="account-meta-row">
                                            <span className="account-meta-label">Адрес</span>
                                            <span className="account-meta-value">
                                                {currentAddress.AddressString}, {currentAddress.County ? `${currentAddress.County}, ` : ''}
                                                {currentAddress.City}
                                            </span>
                                        </div>
                                        <div className="account-meta-row">
                                            <span className="account-meta-label">Контакты</span>
                                            <span className="account-meta-value">
                                                {currentAddress.Index} - {currentAddress.Phone}
                                            </span>
                                        </div>
                                    </div>
                                ) : (
                                    <p className="account-empty">Выберите адрес доставки.</p>
                                )}
                            </div>

                            <div className="account-actions">
                                <button
                                    onClick={() => handleEdit({})}
                                    disabled={addresses.length >= MAX_ADDRESSES}
                                    className="account-button"
                                    type="button"
                                >
                                    Добавить новый адрес
                                </button>
                            </div>
                        </>
                    )}
                </div>
            ) : (
                <div className="account-panel">
                    <div className="account-panel__header">
                        <div>
                            <h3 className="account-panel__title">{editingAddress?.Id ? 'Редактирование адреса' : 'Новый адрес'}</h3>
                            <p className="account-panel__subtitle">Введите адрес и контактный телефон для доставки.</p>
                        </div>
                    </div>
                    <AddressForm
                        editingAddress={editingAddress}
                        handleCancel={handleCancel}
                        register={register}
                        handleSubmit={handleSubmit(onSubmit)}
                        errors={errors}
                    />
                </div>
            )}
        </section>
    );
};

export default AddressBook;
